/**
 * Consultas reativas sobre o IndexedDB.
 *
 * `useLiveQuery` re-executa a consulta quando as tabelas envolvidas mudam, o que
 * mantém favoritos e status offline coerentes entre telas sem um store em memória
 * espelhando o banco (PLAN §49).
 *
 * O valor `undefined` significa "ainda carregando" — nunca "vazio". As telas usam
 * essa diferença para escolher entre esqueleto e estado vazio.
 */
import { useLiveQuery } from 'dexie-react-hooks';
import { db } from '@/db/schema';
import { listSignsByCategory } from '@/db/repositories/catalog';
import { emptyLocalSignState, type Category, type LocalSignState, type Sign } from '@/types/catalog';

export function useCategories(): Category[] | undefined {
  return useLiveQuery(() => db.categories.orderBy('order').toArray(), []);
}

export function useCategory(categoryId: string | undefined): Category | undefined | null {
  return useLiveQuery(
    async () => (categoryId ? ((await db.categories.get(categoryId)) ?? null) : null),
    [categoryId],
  );
}

export function useSignsOfCategory(categoryId: string | undefined): Sign[] | undefined {
  return useLiveQuery(
    async () => (categoryId ? listSignsByCategory(categoryId) : []),
    [categoryId],
  );
}

export function useSign(signId: string | undefined): Sign | undefined | null {
  return useLiveQuery(
    async () => (signId ? ((await db.signs.get(signId)) ?? null) : null),
    [signId],
  );
}

export function useSignState(signId: string | undefined): LocalSignState | undefined {
  return useLiveQuery(
    async () =>
      signId
        ? ((await db.localSignState.get(signId)) ?? emptyLocalSignState(signId))
        : undefined,
    [signId],
  );
}

/** Estados de um conjunto de sinais, indexados por id. */
export function useSignStates(signIds: string[]): Map<string, LocalSignState> | undefined {
  const key = signIds.join('|');

  return useLiveQuery(async () => {
    if (signIds.length === 0) return new Map<string, LocalSignState>();

    const rows = await db.localSignState.bulkGet(signIds);
    const map = new Map<string, LocalSignState>();
    signIds.forEach((signId, index) => {
      const row = rows[index];
      if (row) map.set(signId, row);
    });
    return map;
    // `key` cobre a lista; `signIds` mudaria de identidade a cada render.
  }, [key]);
}

export interface CategoryStats {
  learned: number;
  offlineAvailable: number;
}

/**
 * Agregados por categoria para a Home.
 *
 * Uma consulta única sobre `localSignState` em vez de uma por categoria: são
 * centenas de sinais, e N consultas por render deixariam a lista travada.
 */
export function useCategoryStats(): Map<string, CategoryStats> | undefined {
  return useLiveQuery(async () => {
    const states = await db.localSignState.toArray();
    const relevant = states.filter(
      (state) => state.learned || state.offline.status === 'available',
    );
    if (relevant.length === 0) return new Map<string, CategoryStats>();

    const signs = await db.signs.bulkGet(relevant.map((state) => state.signId));
    const stats = new Map<string, CategoryStats>();

    relevant.forEach((state, index) => {
      const categoryId = signs[index]?.categoryId;
      if (!categoryId) return;

      const current = stats.get(categoryId) ?? { learned: 0, offlineAvailable: 0 };
      if (state.learned) current.learned += 1;
      if (state.offline.status === 'available') current.offlineAvailable += 1;
      stats.set(categoryId, current);
    });

    return stats;
  }, []);
}

export function useFavoriteSigns(): Sign[] | undefined {
  return useLiveQuery(async () => {
    const ids = (await db.localSignState.filter((state) => state.favorite).toArray()).map(
      (state) => state.signId,
    );
    if (ids.length === 0) return [];

    const signs = (await db.signs.bulkGet(ids)).filter((sign): sign is Sign => sign !== undefined);
    return signs.sort((a, b) => a.categoryId.localeCompare(b.categoryId) || a.order - b.order);
  }, []);
}

export interface RecentSign {
  sign: Sign;
  lastViewedAt: string;
}

export function useRecentSigns(limit = 20): RecentSign[] | undefined {
  return useLiveQuery(async () => {
    const rows = await db.localSignState
      .where('lastViewedAt')
      .above('')
      .reverse()
      .limit(limit)
      .toArray();

    const signs = await db.signs.bulkGet(rows.map((row) => row.signId));

    return rows
      .map((row, index) => ({ sign: signs[index], lastViewedAt: row.lastViewedAt ?? '' }))
      .filter((entry): entry is RecentSign => entry.sign !== undefined);
  }, [limit]);
}

/** Vizinhos do sinal dentro da categoria, para navegação anterior/próximo. */
export function useSignNeighbours(
  sign: Sign | null | undefined,
): { previous: Sign | null; next: Sign | null } | undefined {
  return useLiveQuery(async () => {
    if (!sign) return { previous: null, next: null };

    const siblings = await listSignsByCategory(sign.categoryId);
    const index = siblings.findIndex((candidate) => candidate.id === sign.id);

    return {
      previous: index > 0 ? (siblings[index - 1] ?? null) : null,
      next: index >= 0 && index < siblings.length - 1 ? (siblings[index + 1] ?? null) : null,
    };
  }, [sign?.id, sign?.categoryId]);
}

/** Nome das categorias por id, para listas que misturam categorias. */
export function useCategoryNames(): Map<string, string> | undefined {
  return useLiveQuery(async () => {
    const categories = await db.categories.toArray();
    return new Map(categories.map((category) => [category.id, category.name]));
  }, []);
}
